import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FaGithub, FaLinkedin, FaAngellist } from "react-icons/fa";
import Floating from "./FloatingButton";
import LanguageSwitcher from "./languageSwitcher";
import { useLanguage } from "../utils/LanguageContext";
import { useTheme } from "../utils/ThemeContext";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuKey, setMenuKey] = useState(0); // remount menu items for stagger re-animation
  const [scrollProgress, setScrollProgress] = useState(0);
  const [themeFlipping, setThemeFlipping] = useState(false);
  const navigate = useLocation();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setScrolled(scrollY >= 80 && window.location.pathname === "/");

        // Scroll progress
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(docHeight > 0 ? scrollY / docHeight : 0);

        ticking.current = false;
      });
      ticking.current = true;
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const sectionMap = {
      "#projects": "projects",
      "#contact": "contact",
      "#about": "about",
      "#tech": "tech",
      "#experience": "experience",
    };
    const sectionId = sectionMap[navigate.hash] || "home";
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [navigate]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const openMenu = () => {
    setOpen((prev) => {
      if (!prev) setMenuKey((k) => k + 1); // retrigger stagger animation
      return !prev;
    });
  };
  const handleLinkClick = () => setOpen(false);

  const handleToggleTheme = () => {
    setThemeFlipping(true);
    toggleTheme();
    setTimeout(() => setThemeFlipping(false), 400);
  };

  const socialIcons = [
    { id: 1, icon: <FaGithub />, url: "https://github.com/mwafrika", label: "GitHub profile" },
    { id: 2, icon: <FaLinkedin />, url: "https://www.linkedin.com/in/mwafrika-mufungizi", label: "LinkedIn profile" },
    { id: 3, icon: <FaAngellist />, url: "https://angel.co/u/mwafrika-mufungizi", label: "AngelList profile" },
  ];

  return (
    <>
      {/* ── Scroll progress bar ────────────────────────────────────────── */}
      <div
        className="scroll-progress"
        style={{ transform: `scaleX(${scrollProgress})` }}
        aria-hidden="true"
      />

      <header
        className={`${
          scrolled ? "header active" : "header"
        } flex w-full h-20 py-4 px-6 items-center bg-slate-900 dark:bg-white dark:shadow-[0_1px_0_rgba(0,0,0,0.06)] dark:border-b dark:border-slate-200/80 text-gray-400 dark:text-slate-600 justify-between fixed z-[999] top-0 left-0 right-0`}
      >
        {/* Logo */}
        <div className="hidden lg:flex text-white dark:text-slate-900 font-bold text-lg tracking-tight select-none">
          <span className="bg-gradient-to-r from-violet-400 to-sky-400 bg-clip-text text-transparent">
            MW
          </span>
          <span className="text-white dark:text-slate-800 font-semibold">afrika</span>
        </div>

        {/* Desktop Nav */}
        <nav aria-label="Main navigation" className="hidden md:flex">
          <ul className="flex items-center gap-x-1">
            {t.menu.map(({ id, name, url }) => (
              <li key={id}>
                <a
                  href={url}
                  className="nav-link text-sm font-medium text-gray-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-white/5 dark:hover:bg-slate-100 transition-colors duration-200"
                >
                  {name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop social icons */}
        <nav aria-label="Social links" className="hidden md:flex items-center gap-x-1">
          {socialIcons.map(({ id, icon, url, label }) => (
            <a
              key={id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="social-icon text-xl text-gray-400 dark:text-slate-500 hover:text-violet-400 dark:hover:text-violet-600 p-2 rounded-lg hover:bg-white/5 dark:hover:bg-slate-100 transition-colors duration-200"
            >
              {icon}
            </a>
          ))}
        </nav>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={handleToggleTheme}
            aria-label={theme === "dark" ? "Switch to dark mode" : "Switch to light mode"}
            className={`w-9 h-9 flex items-center justify-center rounded-full text-base bg-slate-700/60 dark:bg-slate-100 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 ${themeFlipping ? "theme-toggle-flip" : ""}`}
          >
            {theme === "dark" ? "🌞" : "🌙"}
          </button>
        </div>

        {/* Mobile hamburger — animated bars */}
        <button
          id="hamburger-button"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          className="relative md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-slate-700/60 dark:bg-slate-100 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 z-[1002]"
          onClick={openMenu}
        >
          <div className="flex flex-col justify-center items-center w-5 h-4 gap-[5px]">
            <span
              className={`block h-0.5 bg-white dark:bg-slate-700 rounded-full transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                open ? "w-5 rotate-45 translate-y-[7px]" : "w-5"
              }`}
            />
            <span
              className={`block h-0.5 bg-white dark:bg-slate-700 rounded-full transition-all duration-200 ${
                open ? "opacity-0 w-0" : "opacity-100 w-4"
              }`}
            />
            <span
              className={`block h-0.5 bg-white dark:bg-slate-700 rounded-full transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                open ? "w-5 -rotate-45 -translate-y-[7px]" : "w-5"
              }`}
            />
          </div>
        </button>
      </header>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-[1000] bg-black/60 dark:bg-black/25 backdrop-blur-sm md:hidden transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleLinkClick}
        aria-hidden="true"
      />

      {/* Mobile menu panel */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`fixed z-[1001] top-0 right-0 h-full w-72 bg-slate-900 dark:bg-white shadow-2xl border-l border-slate-800 dark:border-slate-200 transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] md:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700/70 dark:border-slate-200">
          <span className="text-white dark:text-slate-900 font-semibold text-base tracking-tight">
            Menu
          </span>
          <button
            type="button"
            onClick={handleLinkClick}
            aria-label="Close navigation menu"
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-700 dark:bg-slate-100 hover:bg-slate-600 dark:hover:bg-slate-200 text-white dark:text-slate-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="p-5">
          <ul key={menuKey} className="flex flex-col gap-y-1">
            {t.menu.map(({ id, name, url }, index) => (
              <li key={id} className={`menu-item menu-item-${index + 1}`}>
                <a
                  href={url}
                  onClick={handleLinkClick}
                  className="block py-3 px-4 text-base font-medium text-gray-200 dark:text-slate-700 hover:text-white dark:hover:text-violet-600 hover:bg-white/5 dark:hover:bg-slate-50 rounded-xl transition-all duration-200 group"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-violet-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {name}
                  </span>
                </a>
              </li>
            ))}
          </ul>

          <div className="flex gap-x-3 mt-8 pt-5 border-t border-slate-700/70 dark:border-slate-200">
            {socialIcons.map(({ id, icon, url, label }) => (
              <a
                key={id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="social-icon text-xl text-gray-400 dark:text-slate-500 hover:text-violet-400 dark:hover:text-violet-600 p-2.5 rounded-lg hover:bg-white/5 dark:hover:bg-slate-100 transition-colors duration-200"
              >
                {icon}
              </a>
            ))}
          </div>
        </nav>
      </div>

      <Floating />
    </>
  );
};

export default Header;
